import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { DashboardAPI } from '../api/dashboard';
import { TAGS } from '@/entities/srm/tag/@x/dashboard';

import { CreateDashboard, Dashboard, UpdateDashboard } from './dashboard';
import { InterceptedError } from '@/shared/api/intercepted-error';
import { exportFile } from '@/shared/lib/utils/export-file';
import { requestError } from '@/shared/ui/notifications';

export const DASHBOARD_KEY = 'dashboards';

// Inactive list queries (add-tab select) stay cached forever and do not
// refetch on remount. Mutations must refetchType 'all' or F5 is required.
function invalidateDashboardCaches(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [DASHBOARD_KEY],
    refetchType: 'all',
  });
  queryClient.invalidateQueries({
    queryKey: [TAGS, 'list'],
    refetchType: 'all',
  });
}

export function useDashboards() {
  return useQuery({
    queryKey: [DASHBOARD_KEY],
    queryFn: () =>
      DashboardAPI.getDashboardList({ size: -1 }).then((result) => {
        return result?.data?.items ?? [];
      }),

    cacheTime: Infinity,
    refetchOnMount: true,
  });
}

export function useDashboardsTitles() {
  return useQuery({
    queryKey: [DASHBOARD_KEY],
    queryFn: () =>
      DashboardAPI.getDashboardTitles().then((result) => {
        return result?.data ?? [];
      }),

    cacheTime: Infinity,
  });
}

export function useDashboard(name: string) {
  return useQuery({
    queryKey: [DASHBOARD_KEY, name],
    queryFn: () =>
      DashboardAPI.getDashboard(name).then((result) => {
        return result?.data ?? null;
      }),
    enabled: !!name,

    retry: false,
    cacheTime: 0,
  });
}

export function useCloneDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [DASHBOARD_KEY, 'clone'],
    mutationFn: async (name: string) =>
      DashboardAPI.cloneDashboard(name).then((res) => {
        return res?.data;
      }),
    onSuccess: () => {
      invalidateDashboardCaches(queryClient);
    },
  });
}

interface DeleteProps {
  name: string;
  force_flag: boolean;
}

export function useDeleteDashboard(props?: {
  onSuccess?: (data?: string) => void;
  onError?: (data: InterceptedError) => void;
}) {
  const { onSuccess, onError } = props || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [DASHBOARD_KEY, 'delete'],
    mutationFn: async ({ name, force_flag }: DeleteProps) =>
      DashboardAPI.deleteDashboard(name, force_flag).then(() => name),
    onSuccess: (id) => {
      onSuccess?.(id);
      queryClient.removeQueries({ queryKey: [DASHBOARD_KEY, id] });
      invalidateDashboardCaches(queryClient);
    },
    onError: (data: InterceptedError) => {
      onError?.(data);
    },
  });
}

interface CallbackProps<T> {
  onSuccess?: (data?: T) => void;
  onError?: (data: InterceptedError) => void;
}

export function useCreateDashboard(props?: CallbackProps<Dashboard>) {
  const { onSuccess, onError } = props ?? {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [DASHBOARD_KEY, 'create'],
    mutationFn: async (dashboard: CreateDashboard) =>
      DashboardAPI.createDashboard(dashboard).then((res) => {
        // TODO: Поправить после Костиного мержа
        return {
          ...res?.data,
          tags: dashboard.tags,
        };
      }),
    onSuccess: (data) => {
      onSuccess?.(data);
      invalidateDashboardCaches(queryClient);
    },
    onError: (e: InterceptedError) => {
      onError?.(e);
      requestError(e);
    },
  });
}

export function useUpdateDashboard(props?: CallbackProps<Dashboard>) {
  const { onSuccess, onError } = props ?? {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [DASHBOARD_KEY, 'update'],
    mutationFn: async (dashboard: UpdateDashboard) =>
      DashboardAPI.updateDashboard(dashboard).then((res) => {
        return res?.data;
      }),
    onSuccess: (data) => {
      queryClient.setQueryData<Dashboard>([DASHBOARD_KEY, data.id], () => {
        return data;
      });
      invalidateDashboardCaches(queryClient);
      onSuccess?.(data);
    },
    onError: (e: InterceptedError) => {
      onError?.(e);
      requestError(e);
    },
  });
}

export function useExportDashboards() {
  return useMutation({
    mutationKey: [DASHBOARD_KEY, 'export'],
    mutationFn: async (selectedElems: Record<string, boolean>) => {
      const ids = Object.keys(selectedElems);
      return DashboardAPI.exportDashboards(ids).then((res) => res);
    },
    onSuccess: (response) => {
      exportFile(
        JSON.stringify(response.data, null, 4),
        'application/json',
        response.headers['filename'] || 'dashboards.json',
      );
    },
  });
}